import React from 'react';
import { Amount } from '@akropolis-web/primitives';

import { MRT_ColumnDef, amountToAccessorValue } from 'components/MRT';
import { MarketIcon, NextLink, RatingLiteral, FallbackedTotalUSD, MarketName } from 'components';
import { colors, styled } from 'core/styles';
import { ratingToLetter } from 'domain/types';
import { FormattedAmountProps } from 'components/FormattedAmount/types';
import { useRouteUtils } from 'routes';
import { MarketsTableData } from 'api/selectors/pages';

const shortFormattedAmountProps: Omit<FormattedAmountProps<Amount>, 'sum'> = {
  precision: 2,
  withSI: true,
  siPrecision: 'million',
};

export const columns: MRT_ColumnDef<MarketsTableData>[] = [
  {
    Header: ({ table }) => (
      <>
        <MarketHeader>Markets</MarketHeader> · {table.options.data.length} most vulnerable
      </>
    ),
    header: 'Markets',
    accessorFn: originalRow => (
      <MarketNameContainer>
        <MarketIconBox>
          <MarketIcon
            productSlug={originalRow.productSlug}
            versionSlug={originalRow.versionSlug}
            network={originalRow.network}
            marketId={originalRow.marketId}
            tokens={originalRow.tokens}
            fontSize="inherit"
          />
        </MarketIconBox>
        <MarketName marketName={originalRow.marketName} maxWidth={180} />
      </MarketNameContainer>
    ),
    id: 'market',
    size: 220,
    enableColumnActions: false,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    header: 'Rating',
    accessorFn: originalRow => originalRow.rating.fold(ratingToLetter, () => undefined),
    id: 'rating',
    size: 100,
    enableColumnActions: false,
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ row }) => <RatingLiteral rating={row.original.rating} colored />,
    muiTableHeadCellProps: {
      sx: {
        paddingLeft: '12px',
      },
    },
    muiTableBodyCellProps: {
      align: 'left',
      sx: {
        paddingLeft: '12px',
        fontWeight: 500,
      },
    },
  },
  {
    header: 'TVL',
    accessorFn: originalRow => amountToAccessorValue(originalRow.totalDeposit.value),
    id: 'tvl',
    size: 80,
    enableColumnActions: false,
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ row }) => (
      <FallbackedTotalUSD
        {...row.original.totalDeposit}
        formattedAmountProps={shortFormattedAmountProps}
      />
    ),
  },
  {
    header: '',
    id: 'links',
    size: 50,
    enableColumnActions: false,
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ row }) => {
      const { redirectTo } = useRouteUtils();

      return (
        <BaseLink
          {...redirectTo.market({
            productSlug: row.original.productSlug,
            versionSlug: row.original.versionSlug,
            network: row.original.network,
            marketId: row.original.marketId,
          })}
        >
          View market
        </BaseLink>
      );
    },
  },
];

const MarketHeader = styled('span')({
  color: colors.black,
});

const MarketIconBox = styled('div')(() => ({
  fontSize: 24,
}));

const MarketNameContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
}));

const BaseLink = styled(NextLink)({
  textDecoration: 'underline',
  transition: 'color 200ms',

  '&:hover': {
    color: colors.blackOpacity[40],
  },
});
